import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { StepBlock } from '../../../components/StepBlock/StepBlock';
import { Button } from '../../../components/Button/Button';
import edit from 'assets/images/icons/edit.svg';
import eyeLight from 'assets/images/icons/eye-light.svg';
import trashCan from 'assets/images/icons/trashcan.svg';
import download from 'assets/images/icons/download.svg';
import arrowPrev from 'assets/images/icons/arrow-prev.svg';
import arrowNext from 'assets/images/icons/arrow-next.svg';
import uploadIcon from 'assets/images/icons/upload.svg';
import { CreatePersonPayload, Person } from 'services/person/person.types';
import { FileUploader } from 'components/FileUploader/FileUploader';
import { ApiFile } from 'utils/useFlowTs';
import Table from 'components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import { Modal } from 'components/Modal';
import { createNewPersonSecondStepSchema } from '../NewPersonFormsSchema';
import { downloadFileAsBlob } from 'utils/blob';

interface NewPersonSecondStepProps {
    handleNextStep: (data: CreatePersonPayload) => void;
    handlePreviousStep: (data: CreatePersonPayload) => void;
    setActiveStep: (step: number) => void;
    person: Person;
}

export const NewPersonSecondStep: React.FC<NewPersonSecondStepProps> = ({ handleNextStep, handlePreviousStep, setActiveStep, person }) => {
    const [files, setFiles] = useState<ApiFile[]>([]);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
    const [localDocuments, setLocalDocuments] = useState<ApiFile[]>([]);
    const [documentModifications, setDocumentModifications] = useState<ApiFile[]>([]);

    useEffect(() => {
        if (person?.documents) {
            setLocalDocuments(person.documents as unknown as ApiFile[]);
        }
    }, [person?.documents]);

    const columnHelper = createColumnHelper<ApiFile>();

    const tableColumns = [
        columnHelper.accessor('label', {
            header: 'Nom du fichier',
            cell: (info) => info.getValue()
        }),
        columnHelper.display({
            header: 'Actions',
            cell: ({ row: { original: currRow } }) => {
                return (
                    <span style={{ display: 'flex', columnGap: 12 }}>
                        <img
                            src={edit}
                            alt="Modifier le document"
                            onClick={() => {
                                setDocumentModifications([currRow]);
                                setIsDocumentModalOpen(true);
                            }}
                        />
                        <img
                            src={eyeLight}
                            alt="Prévisualiser le document"
                            onClick={() => {
                                if (currRow.view_link) {
                                    window.open(currRow.view_link, '_blank');
                                }
                            }}
                        />
                        <img
                            src={trashCan}
                            alt="Supprimer le document"
                            onClick={() => {
                                setLocalDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== currRow.id));
                            }}
                        />
                        <img
                            src={download}
                            alt="Télécharger le document"
                            onClick={() => {
                                downloadFileAsBlob(currRow).catch((error: any) => {
                                    console.error('Erreur lors du téléchargement:', error);
                                });
                            }}
                        />
                    </span>
                );
            }
        })
    ];

    return (
        <>
            <Formik
                initialValues={{
                    firstname: person?.firstname || '',
                    lastname: person?.lastname || '',
                    phone: person?.phone || '',
                    documents: localDocuments
                }}
                enableReinitialize={true}
                validationSchema={createNewPersonSecondStepSchema()}
                onSubmit={async (values: CreatePersonPayload, { setSubmitting }) => {
                    await handleNextStep(values);
                    setSubmitting(false);
                }}
            >
                {({ values, errors, handleBlur, handleSubmit, setFieldValue }) => {
                    function handleFileUploadSuccess(file: ApiFile) {
                        setLocalDocuments((prevDocs) => [...prevDocs, file]);
                        setFieldValue('documents', [...(values.documents || []), file]);
                    }

                    return (
                        <>
                            <form onSubmit={handleSubmit} className="form">
                                <StepBlock
                                    title="Gestion électronique des documents (GED)"
                                    subtitle="Ajoutez ici les documents utiles au dossier de la personne soutenue"
                                >
                                    {localDocuments && localDocuments.length > 0 ? (
                                        <Table data={localDocuments} columns={tableColumns} columnHelper={columnHelper} selectable />
                                    ) : (
                                        <FileUploader files={files} setFiles={setFiles} onSuccess={handleFileUploadSuccess} size="big" />
                                    )}
                                    {localDocuments && localDocuments.length > 0 && (
                                        <FileUploader
                                            files={files}
                                            setFiles={setFiles}
                                            onSuccess={handleFileUploadSuccess}
                                            label="Importer un document supplémentaire"
                                            variant="light"
                                            icon={<img src={uploadIcon} alt="Importer un document supplémentaire" />}
                                        />
                                    )}
                                    <p className="accepted-file">
                                        Types de fichiers acceptés : pdf, docx, doc, xlsx, xls, pptx, ppt, txt, csv, jpg, jpeg, png, gif.
                                    </p>
                                </StepBlock>
                                <div className="actions-buttons">
                                    <Button
                                        type="button"
                                        label="Retour à l'étape précédente"
                                        icon={<img src={arrowPrev} alt="Retour à l'étape précédente" />}
                                        iconBefore
                                        onClick={(e) => {
                                            handlePreviousStep(values);
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        label="Passer à l'étape suivante"
                                        icon={<img src={arrowNext} alt="Passer à l'étape suivante" />}
                                        iconAfter
                                    />
                                </div>
                            </form>
                            <Modal
                                open={isDocumentModalOpen}
                                setClose={() => {
                                    setIsDocumentModalOpen(false);
                                    setDocumentModifications([]);
                                }}
                                title="Modifier les informations du document"
                                yesText="Modifier"
                                noText="Annuler"
                                onValidate={() => {
                                    setLocalDocuments((prev) =>
                                        prev.map((doc) => (doc.id === documentModifications[0]?.id ? documentModifications[0] : doc))
                                    );
                                    setFieldValue(
                                        'documents',
                                        values.documents?.map((doc) =>
                                            doc.id === documentModifications[0]?.id ? documentModifications[0] : doc
                                        )
                                    );
                                    setIsDocumentModalOpen(false);
                                    setDocumentModifications([]);
                                }}
                                onClose={() => {
                                    setIsDocumentModalOpen(false);
                                    setDocumentModifications([]);
                                }}
                            >
                                <div className="form">
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Nom du fichier</label>
                                            <input
                                                type="text"
                                                name="label"
                                                onChange={(e) => {
                                                    const newLabel = e.target.value;
                                                    setDocumentModifications((prev) => {
                                                        const updatedModifications = [...prev];
                                                        if (updatedModifications[0]) {
                                                            updatedModifications[0] = {
                                                                ...updatedModifications[0],
                                                                label: newLabel
                                                            };
                                                        }
                                                        return updatedModifications;
                                                    });
                                                }}
                                                onBlur={handleBlur}
                                                value={documentModifications[0]?.label || ''}
                                            />
                                            {typeof errors.documents === 'object' && (errors.documents as any).label && (
                                                <p className="error">{(errors.documents as any).label}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Description</label>
                                            <textarea
                                                name="observation"
                                                onChange={(e) => {
                                                    const newObservation = e.target.value;
                                                    setDocumentModifications((prev) => {
                                                        const updatedModifications = [...prev];
                                                        if (updatedModifications[0]) {
                                                            updatedModifications[0] = {
                                                                ...updatedModifications[0],
                                                                observation: newObservation
                                                            };
                                                        }
                                                        return updatedModifications;
                                                    });
                                                }}
                                                onBlur={handleBlur}
                                                value={documentModifications[0]?.observation || ''}
                                                rows={6}
                                            />
                                            {typeof errors.documents === 'object' && (errors.documents as any).observation && (
                                                <p className="error">{(errors.documents as any).observation}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Niveau de confidentialité</label>
                                            <div className="radios">
                                                <label className="radio">
                                                    <input
                                                        type="radio"
                                                        name="level"
                                                        onChange={(e) => {
                                                            setDocumentModifications((prev) => {
                                                                const updatedModifications = [...prev];
                                                                if (updatedModifications[0]) {
                                                                    updatedModifications[0] = {
                                                                        ...updatedModifications[0],
                                                                        level: 'public'
                                                                    };
                                                                }
                                                                return updatedModifications;
                                                            });
                                                        }}
                                                        checked={documentModifications[0]?.level === 'public'}
                                                        value="public"
                                                    />
                                                    Public
                                                </label>
                                                <label className="radio">
                                                    <input
                                                        type="radio"
                                                        name="level"
                                                        onChange={(e) => {
                                                            setDocumentModifications((prev) => {
                                                                const updatedModifications = [...prev];
                                                                if (updatedModifications[0]) {
                                                                    updatedModifications[0] = {
                                                                        ...updatedModifications[0],
                                                                        level: 'private'
                                                                    };
                                                                }
                                                                return updatedModifications;
                                                            });
                                                        }}
                                                        checked={documentModifications[0]?.level === 'private'}
                                                        value="private"
                                                    />
                                                    Privé
                                                </label>
                                            </div>
                                            {typeof errors.documents === 'object' && (errors.documents as any).level && (
                                                <p className="error">{(errors.documents as any).level}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        </>
                    );
                }}
            </Formik>
        </>
    );
};
